import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  api,
  ApiError,
  DEFAULT_QUOTHA_MODE,
  type AnswerMeta,
  type FigureLite,
  type QuothaBundle,
  type QuothaMode,
  type QuothaPanelPayload,
  type QuothaStatus,
  type Thread,
  type ThreadMessage,
  type ThreadSettingsPayload,
  type UserSettingsResponse,
} from '../api'
import { useAuth } from '../auth/AuthProvider'
import {
  getKnowledgeScopeLabel,
  getLanguageModeLabel,
  getToneLevelLabel,
  normalizeLanguageMode,
  type NormalizedLanguageModeOption,
} from '../constants/chatPreferences'
import { DEFAULT_SETTINGS, type BaseSettings } from '../constants/defaultSettings'
import { getApiBase, resolveMediaUrl } from '../utils/media'
import {
  ensureValidFigureSlug,
  loadFigureSlugRegistry,
  OUT_OF_DATE_FIGURE_MESSAGE,
  primeFigureSlugRegistry,
} from '../utils/figureSlugRegistry'
import { normalizeQuothaPanelPayload } from '../utils/quotha'
import QuickControlsCard from '../components/QuickControlsCard'
import QuothaPanel from '../components/QuothaPanel'
import ContextMatchBadge, { normalizeContextMatchBand, type ContextMatchBand } from '../components/ContextMatchBadge'
import SourceDepthBadge from '../components/SourceDepthBadge'
import TypewriterReveal from '../chat/components/TypewriterReveal'
import TypingAnimationToggle from '../chat/components/TypingAnimationToggle'
import useTypingAnimationPreference from '../chat/hooks/useTypingAnimationPreference'

type SettingsValues = BaseSettings & { quotha_mode: QuothaMode; quotha_annotations?: boolean }

const DEFAULT_THREAD_SETTINGS: SettingsValues = {
  ...DEFAULT_SETTINGS,
  quotha_mode: DEFAULT_QUOTHA_MODE,
  quotha_annotations: false,
}

const normalizeQuothaMode = (value?: Thread['quotha_mode']): QuothaMode => {
  if (value === 'on' || value === 'off') return value
  return DEFAULT_QUOTHA_MODE
}

const mergeSettings = (response: UserSettingsResponse | null): { values: SettingsValues; defaults: SettingsValues } => {
  const defaults: SettingsValues = {
    ...DEFAULT_THREAD_SETTINGS,
    ...(response?.defaults ?? {}),
    language_mode: normalizeLanguageMode((response?.defaults ?? {}).language_mode ?? DEFAULT_SETTINGS.language_mode),
    quotha_mode: DEFAULT_QUOTHA_MODE,
    quotha_annotations: response?.defaults?.quotha_annotations ?? response?.quotha_annotations ?? false,
  }

  return {
    defaults,
    values: {
      language_mode: normalizeLanguageMode(response?.language_mode ?? defaults.language_mode),
      knowledge_scope: response?.knowledge_scope ?? defaults.knowledge_scope,
      tone_level: response?.tone_level ?? defaults.tone_level,
      quotha_mode: DEFAULT_QUOTHA_MODE,
      quotha_annotations: response?.quotha_annotations ?? defaults.quotha_annotations ?? false,
    },
  }
}

const API_BASE = getApiBase()

const normalizeImageSrc = (value?: string | null) => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

const buildInitials = (value?: string | null) => {
  if (!value) return 'PiT'
  const parts = value.trim().split(/\s+/)
  const initials = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('')
  return initials || 'PiT'
}

type SafeFigureThumbProps = {
  rawSrc?: string | null
  alt?: string
  size: number
  variant?: 'circle' | 'rounded'
  fallbackText?: string
  cacheKey: string
}

const SafeFigureThumb = ({ rawSrc, alt = 'Figure portrait', size, variant = 'circle', fallbackText, cacheKey }: SafeFigureThumbProps) => {
  const [src, setSrc] = useState<string | null>(normalizeImageSrc(rawSrc))
  const [retried, setRetried] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)

  useEffect(() => {
    setSrc(normalizeImageSrc(rawSrc))
    setRetried(false)
    setLoaded(false)
  }, [rawSrc])

  useEffect(() => {
    const img = imgRef.current
    if (!img) return
    if (!loaded && img.complete && img.naturalWidth > 0) {
      setLoaded(true)
    }
  }, [src, loaded])

  const initials = useMemo(() => buildInitials(fallbackText || alt), [fallbackText, alt])

  const handleError = () => {
    if (!src) return
    if (!retried) {
      const cacheBusted = `${src}${src.includes('?') ? '&' : '?'}v=${Date.now()}`
      setSrc(cacheBusted)
      setRetried(true)
      setLoaded(false)
      return
    }
    setSrc(null)
  }

  const classes = ['figure-thumb', variant].filter(Boolean).join(' ')

  if (!src) {
    return (
      <span className={classes} style={{ width: size, height: size }}>
        <span className="figure-thumb-monogram">{initials}</span>
      </span>
    )
  }

  return (
    <span className={classes} style={{ width: size, height: size }}>
      <img
        key={`${cacheKey}:${src}`}
        className={`figure-thumb-image${loaded ? ' is-loaded' : ''}`}
        src={src}
        alt={alt}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        fetchPriority="low"
        style={{ objectFit: 'cover', width: size, height: size }}
        ref={imgRef}
        onError={handleError}
        onLoad={() => setLoaded(true)}
      />
    </span>
  )
}

type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  created_at: string
  quotha_panel?: QuothaPanelPayload | null
  quotha?: QuothaBundle
  quotha_status?: QuothaStatus
  quotha_error_ui?: string | null
  sources?: NormalizedSource[]
  rawSources?: unknown[]
  answer_meta?: AnswerMeta
  sources_available?: boolean
  sources_count?: number
  works_present?: string[]
  knowledge_basis?: string[] | null
  epistemic?: NormalizedEpistemic
}

type NormalizedSource = {
  label: string
  url?: string
  meta?: string
  snippet?: string
  passage?: string
  note?: string
  chunkId?: string
  docId?: string
  workTitle?: string
  sourceName?: string
  sourceType?: string
  corpusLane?: string
  contentType?: string
  section?: string
  chunkIndex?: string
  publicationDate?: string
  activeVersion?: string
}

type NormalizedEpistemic = {
  retrievalSimilarityBand?: ContextMatchBand
  activeRagVersion?: string
  uncertaintyFlags?: string[]
}

type SnippetModalState = {
  source: NormalizedSource
  sourceTitle: string
  sourceByline?: string
  sourceUrl?: string
  sourceType?: string
  partLabel?: string
  publicationDate?: string
  activeVersion?: string
  excerptText?: string
  fullChunkText?: string
  loading: boolean
  error?: string
}

const normalizeWorksPresent = (value?: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
}

const uniqueSorted = (items: string[]): string[] => {
  const deduped = Array.from(new Set(items))
  return deduped.sort((a, b) => a.localeCompare(b))
}

const coerceSourceText = (value?: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

const normalizeUncertaintyFlags = (value?: unknown): string[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim()
      if (!item || typeof item !== 'object') return ''
      const record = item as Record<string, unknown>
      return (
        coerceSourceText(record.message) ??
        coerceSourceText(record.label) ??
        coerceSourceText(record.flag) ??
        coerceSourceText(record.code) ??
        ''
      )
    })
    .filter((item) => item.length > 0)
}

const normalizeEpistemic = (value?: unknown): NormalizedEpistemic | undefined => {
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  const retrievalSimilarityBand = normalizeContextMatchBand(record.retrieval_similarity_band ?? record.retrievalSimilarityBand) ?? undefined
  const activeRagVersion = coerceSourceText(record.active_rag_version ?? record.activeRagVersion) ?? undefined
  const uncertaintyFlags = normalizeUncertaintyFlags(record.uncertainty_flags ?? record.uncertaintyFlags)

  if (!retrievalSimilarityBand && !activeRagVersion && uncertaintyFlags.length === 0) {
    return undefined
  }

  return {
    retrievalSimilarityBand,
    activeRagVersion,
    uncertaintyFlags: uncertaintyFlags.length ? uncertaintyFlags : undefined,
  }
}

const titleCaseSourceLabel = (value?: string | null): string | null => {
  const text = coerceSourceText(value)
  if (!text) return null
  return text
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const buildSourceMeta = (workTitle: string | null, section: string | null, label: string | null): string | null => {
  if (!workTitle || !section) return null
  const labelIncludesWork = Boolean(label && label.toLowerCase().includes(workTitle.toLowerCase()))
  if (labelIncludesWork) return null
  return `${workTitle} · ${section}`
}

const normalizeDisplayString = (value?: string | null): string | null => {
  const text = coerceSourceText(value)
  if (!text) return null
  const withoutPrefix = text.replace(/^(?:src|source|work|doc):\s*/i, '')
  const cleaned = withoutPrefix
    .replace(/\.(?:txt|pdf|md|html?)$/i, '')
    .replace(/\bEntire Play\b/gi, '')
    .replace(/\s*[-|]\s*$/u, '')
    .replace(/[_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!cleaned) return null
  if (/^(?:urlsha|src|work):?[a-z0-9:-]+$/i.test(cleaned)) return null
  return cleaned
}

const normalizeComparableLabel = (value?: string | null): string => (
  normalizeDisplayString(value) ?? ''
).toLowerCase().replace(/[^a-z0-9]+/g, '')

const isNearDuplicateLabel = (a?: string | null, b?: string | null): boolean => {
  const left = normalizeComparableLabel(a)
  const right = normalizeComparableLabel(b)
  if (!left || !right) return false
  if (left === right) return true
  const shorter = left.length < right.length ? left : right
  const longer = left.length < right.length ? right : left
  return shorter.length >= 8 && longer.includes(shorter)
}

const getSourceDisplayTitle = (source: NormalizedSource): string => {
  return normalizeDisplayString(source.workTitle) ??
    normalizeDisplayString(source.label) ??
    normalizeDisplayString(source.sourceName) ??
    'Source'
}

const getSourceByline = (source: NormalizedSource): string | undefined => {
  const title = getSourceDisplayTitle(source)
  const candidate = normalizeDisplayString(source.sourceName)
  if (!candidate || isNearDuplicateLabel(candidate, title)) return undefined
  return candidate
}

const getSourceTypeLabel = (source: NormalizedSource): string | undefined => {
  return source.sourceType ?? source.corpusLane ?? source.contentType
}

const getSourcePartLabel = (source: NormalizedSource): string | undefined => {
  const section = normalizeDisplayString(source.section)
  if (section) return section.replace(/\bchunk\b/gi, 'Passage')
  return source.chunkIndex ? `Passage ${source.chunkIndex}` : undefined
}

const trimLeadingBrokenFragment = (value?: string): string | undefined => {
  const text = coerceSourceText(value)
  if (!text) return undefined
  const punctTrimmed = text.replace(/^[,;:)\]}…\-\s]+/u, '').trimStart()
  const startsMidSentence = /^[a-z]/u.test(punctTrimmed)
  const boundary = punctTrimmed.search(/[.!?]\s+["'“‘(]?[A-Z0-9]/u)
  if (startsMidSentence && boundary >= 20 && boundary <= 180 && punctTrimmed.length - boundary > 80) {
    return punctTrimmed.slice(boundary + 1).trimStart()
  }
  return punctTrimmed
}

const getSourceModalText = (source: NormalizedSource): { excerptText?: string; fullChunkText?: string } => {
  const preview = trimLeadingBrokenFragment(source.snippet ?? source.note)
  const full = trimLeadingBrokenFragment(source.passage)
  const excerptText = preview ?? full
  const fullChunkText = full && full !== excerptText ? full : undefined
  return { excerptText, fullChunkText }
}

const normalizeSource = (raw: unknown): NormalizedSource | null => {
  if (!raw || typeof raw !== 'object') return null
  const source = raw as Record<string, unknown>
  const workTitle =
    coerceSourceText(source.work_title) ??
    coerceSourceText(source.preferred_work_title) ??
    coerceSourceText(source.inferred_work_title) ??
    coerceSourceText(source.title) ??
    null
  const sourceName =
    coerceSourceText(source.attribution) ??
    coerceSourceText(source.raw_source_name) ??
    coerceSourceText(source.source_title) ??
    null
  const label =
    coerceSourceText(source.source_label) ??
    workTitle ??
    sourceName ??
    coerceSourceText(source.source_name) ??
    coerceSourceText(source.label) ??
    coerceSourceText(source.name) ??
    'Source'
  const url = coerceSourceText(source.source_url ?? source.url) ?? undefined
  const snippet = coerceSourceText(source.display_excerpt ?? source.snippet) ?? undefined
  const passage =
    coerceSourceText(source.passage) ??
    coerceSourceText(source.full_passage) ??
    coerceSourceText(source.content) ??
    coerceSourceText(source.chunk_text) ??
    undefined
  const note = coerceSourceText(source.note) ?? undefined
  const chunkId = coerceSourceText(source.chunk_id ?? source.chunkId) ?? undefined
  const docId = coerceSourceText(source.doc_id ?? source.docId) ?? undefined
  const sourceType = titleCaseSourceLabel(coerceSourceText(source.source_type)) ?? undefined
  const corpusLane = titleCaseSourceLabel(coerceSourceText(source.corpus_lane)) ?? undefined
  const contentType = titleCaseSourceLabel(coerceSourceText(source.content_type)) ?? undefined
  const section = coerceSourceText(source.section) ?? undefined
  const chunkIndex =
    typeof source.chunk_index === 'number'
      ? String(source.chunk_index)
      : coerceSourceText(source.chunk_index ?? source.chunkIndex) ?? undefined
  const publicationDate =
    coerceSourceText(source.source_pub_date ?? source.publication_date ?? source.publicationDate ?? source.published_at ?? source.date) ?? undefined
  const activeVersion =
    coerceSourceText(source.active_version ?? source.activeVersion ?? source.active_rag_version) ?? undefined
  const meta = buildSourceMeta(workTitle, section, label)

  if (!label && !url && !snippet && !passage && !note && !meta) return null

  return {
    label: normalizeDisplayString(label) || 'Source',
    url,
    meta: meta ?? undefined,
    snippet,
    passage,
    note,
    chunkId,
    docId,
    workTitle: normalizeDisplayString(workTitle) ?? undefined,
    sourceName: normalizeDisplayString(sourceName) ?? undefined,
    sourceType,
    corpusLane,
    contentType,
    section: normalizeDisplayString(section) ?? undefined,
    chunkIndex,
    publicationDate,
    activeVersion,
  }
}

const normalizeSources = (sources?: unknown): NormalizedSource[] => {
  if (!Array.isArray(sources)) return []
  return sources
    .map((item) => normalizeSource(item))
    .filter((item): item is NormalizedSource => Boolean(item))
}

const deriveWorksFromSources = (sources?: unknown): string[] => {
  if (!Array.isArray(sources)) return []
  const works: string[] = []
  sources.forEach((item) => {
    if (!item || typeof item !== 'object') return
    const workTitle = coerceSourceText((item as Record<string, unknown>).work_title)
    if (workTitle) works.push(workTitle)
  })
  return uniqueSorted(works)
}

const missingSourcesWarnings = new Set<string>()

const getMessageSourcesMeta = (msg: ChatMessage) => {
  const explicitCount =
    typeof msg.answer_meta?.sources_count === 'number'
      ? msg.answer_meta.sources_count
      : typeof msg.sources_count === 'number'
        ? msg.sources_count
        : undefined
  const inferredCount = Array.isArray(msg.sources) ? msg.sources.length : 0
  const sourcesCount = Math.max(0, explicitCount ?? inferredCount)
  const hasSources = sourcesCount > 0 || inferredCount > 0
  return {
    sourcesCount,
    hasSources,
  }
}

const getMessageWorksPresent = (msg: ChatMessage): string[] => {
  return normalizeWorksPresent(msg.answer_meta?.works_present ?? msg.works_present)
}

const mapThreadMessage = (msg: ThreadMessage, index: number): ChatMessage | null => {
  if (!msg?.content) return null
  const role = msg.role === 'assistant' ? 'assistant' : 'user'
  const panel = normalizeQuothaPanelPayload(msg.quotha?.panel ?? msg.quotha_panel)
  const rawSources = Array.isArray(msg.sources) ? msg.sources : []
  const sources = normalizeSources(rawSources)
  const worksPresentInput = normalizeWorksPresent(msg.answer_meta?.works_present ?? msg.works_present)
  const worksPresent = worksPresentInput.length ? uniqueSorted(worksPresentInput) : deriveWorksFromSources(rawSources)
  const sourcesAvailable = msg.answer_meta?.sources_available ?? msg.sources_available
  const epistemic = normalizeEpistemic(msg.epistemic)
  const sourcesCount = typeof msg.answer_meta?.sources_count === 'number'
    ? msg.answer_meta.sources_count
    : typeof msg.sources_count === 'number'
      ? msg.sources_count
      : undefined
  const quothaStatus = normalizeQuothaStatus(msg.quotha_status ?? msg.quotha?.status ?? (panel ? 'ready' : 'none'))
  return {
    id: msg.id ?? `msg-${index}`,
    role,
    content: msg.content,
    created_at: msg.created_at ?? new Date().toISOString(),
    quotha_panel: panel,
    quotha: msg.quotha ?? {
      flags: [],
      panel,
      status: quothaStatus,
    },
    quotha_status: quothaStatus,
    sources,
    rawSources,
    answer_meta: msg.answer_meta,
    sources_available: sourcesAvailable,
    sources_count: sourcesCount ?? sources.length,
    works_present: worksPresent.length ? worksPresent : undefined,
    knowledge_basis: msg.knowledge_basis ?? msg.answer_meta?.knowledge_basis ?? null,
    epistemic,
  }
}

const normalizeLanguageOverride = (value?: Thread['language_mode_override']): NormalizedLanguageModeOption | null => {
  if (value === null || value === undefined) return null
  return normalizeLanguageMode(value)
}

const normalizeThread = (thread: Thread | null): Thread | null => {
  if (!thread) return null
  return {
    ...thread,
    language_mode_override: normalizeLanguageOverride(thread.language_mode_override),
    quotha_mode: normalizeQuothaMode(thread.quotha_mode),
  }
}

const resolveQuothaEnabled = (value?: Thread['quotha_mode'] | null): boolean => normalizeQuothaMode(value) !== 'off'

const toQuothaMode = (enabled: boolean): QuothaMode => (enabled ? 'on' : 'off')

const normalizeQuothaStatus = (value?: unknown): QuothaStatus => {
  if (value === 'pending' || value === 'ready' || value === 'failed') return value
  return 'none'
}

const resolveSourceText = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

const SOURCES_VISIBILITY_PREFIX = 'pit_sources_visibility:'

const makeSourcesStorageKey = (threadKey: string | null): string | null => {
  if (!threadKey) return null
  return `${SOURCES_VISIBILITY_PREFIX}${threadKey}`
}

const readSourcesVisibility = (threadKey: string | null): Record<string, boolean> => {
  const storageKey = makeSourcesStorageKey(threadKey)
  if (!storageKey || typeof window === 'undefined') {
    return {}
  }

  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const result: Record<string, boolean> = {}
    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value !== 'boolean' || !key) return
      const normalizedKey = key.includes(':') ? key : `id:${key}`
      result[normalizedKey] = value
    })
    return result
  } catch {
    return {}
  }
}

const writeSourcesVisibility = (threadKey: string | null, map: Record<string, boolean>) => {
  const storageKey = makeSourcesStorageKey(threadKey)
  if (!storageKey || typeof window === 'undefined') {
    return
  }

  try {
    const payload = Object.fromEntries(
      Object.entries(map).filter(([key, value]) => Boolean(key) && typeof value === 'boolean'),
    )
    window.localStorage.setItem(storageKey, JSON.stringify(payload))
  } catch {
    // ignore storage failures
  }
}

const hashString = (value: string): string => {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash).toString(36)
}

const getMessageStorageKey = (msg: ChatMessage, fallbackIndex: number): string => {
  if (msg.id) return `id:${msg.id}`
  const created = msg.created_at ? `ts:${msg.created_at}` : `idx:${fallbackIndex}`
  const contentHash = msg.content ? hashString(msg.content) : 'content:none'
  return `${msg.role}:${created}:${contentHash}:${fallbackIndex}`
}

const ChatThread = () => {
  const { threadId } = useParams()
  const threadStorageKey = threadId ? String(threadId) : null
  const navigate = useNavigate()
  const { token, ready, isAuthenticated } = useAuth()

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [accountSettings, setAccountSettings] = useState<SettingsValues>(DEFAULT_THREAD_SETTINGS)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [threadInfo, setThreadInfo] = useState<Thread | null>(null)
  const [figure, setFigure] = useState<FigureLite | null>(null)
  const [threadLoading, setThreadLoading] = useState(false)
  const [threadError, setThreadError] = useState<string | null>(null)
  const [slugValidationError, setSlugValidationError] = useState<string | null>(null)
  const [historyNotice, setHistoryNotice] = useState<string | null>(null)
  const [pendingSettings, setPendingSettings] = useState<SettingsValues>(DEFAULT_THREAD_SETTINGS)
  const [controlsExpanded, setControlsExpanded] = useState(false)
  const [controlsSaving, setControlsSaving] = useState(false)
  const [controlsSaved, setControlsSaved] = useState(false)
  const [controlsError, setControlsError] = useState<string | null>(null)
  const [quothaPanels, setQuothaPanels] = useState<Record<string, { panel: QuothaPanelPayload | null; visible: boolean; source: 'auto' | 'manual'; dismissed?: boolean }>>({})
  const [quothaCommentaryEnabled, setQuothaCommentaryEnabled] = useState(false)
  const [sourcesHidden, setSourcesHidden] = useState<Record<string, boolean>>({})
  const [snippetModal, setSnippetModal] = useState<SnippetModalState | null>(null)
  const [, forceAssistantAnimationRerender] = useState(0)

  const toggleControlsExpanded = useCallback(() => {
    setControlsExpanded((prev) => !prev)
  }, [])

  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const sendingRef = useRef(false)
  const pollingMessagesRef = useRef<Set<string>>(new Set())
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const pinnedToBottomRef = useRef(true)
  const animatedAssistantMessageIdsRef = useRef<Set<string>>(new Set())
  const animatedQuothaPanelIdsRef = useRef<Set<string>>(new Set())
  const hydratingHistoryRef = useRef(false)
  const sourcesVisibilityRef = useRef<Record<string, boolean>>({})
  const pendingHistoryRenderRef = useRef(false)
  const snippetRequestIdRef = useRef(0)

  const {
    typingAnimationEnabled,
    typingAnimationPreference,
    typingAnimationBlocked,
    setTypingAnimationEnabled,
  } = useTypingAnimationPreference()

  const seedAssistantMessages = useCallback((list: ChatMessage[]) => {
    list.forEach((msg) => {
      if (msg.role === 'assistant' && typeof msg.id === 'string') {
        animatedAssistantMessageIdsRef.current.add(msg.id)
      }
    })
  }, [])

  const seedQuothaPanels = useCallback((list: ChatMessage[]) => {
    list.forEach((msg) => {
      if (msg.role === 'assistant' && typeof msg.id === 'string' && msg.quotha_panel) {
        animatedQuothaPanelIdsRef.current.add(msg.id)
      }
    })
  }, [])

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth', force = false) => {
    if (!messagesEndRef.current) return
    if (!force && !pinnedToBottomRef.current) return
    messagesEndRef.current.scrollIntoView({ behavior, block: 'end' })
  }, [])

  const registerAssistantMessage = useCallback((messageId: string) => {
    if (!messageId) return
    if (!animatedAssistantMessageIdsRef.current.has(messageId)) {
      animatedAssistantMessageIdsRef.current.add(messageId)
    }
  }, [])

  const markAssistantAnimationComplete = useCallback(
    (messageId: string) => {
      if (!messageId) return
      if (animatedAssistantMessageIdsRef.current.has(messageId)) return
      animatedAssistantMessageIdsRef.current.add(messageId)
      forceAssistantAnimationRerender((prev) => prev + 1)
    },
    [forceAssistantAnimationRerender],
  )

  const persistSourcesVisibility = useCallback(
    (storageKey: string, hidden: boolean) => {
      if (!storageKey) return
      sourcesVisibilityRef.current = {
        ...sourcesVisibilityRef.current,
        [storageKey]: hidden,
      }
      writeSourcesVisibility(threadStorageKey, sourcesVisibilityRef.current)
    },
    [threadStorageKey],
  )

  const threadOverrides = useMemo(() => {
    return {
      language_mode: normalizeLanguageOverride(threadInfo?.language_mode_override),
      knowledge_scope: threadInfo?.knowledge_scope_override ?? null,
      tone_level: threadInfo?.tone_level_override ?? null,
    }
  }, [threadInfo])

  const effectiveSettings = useMemo<BaseSettings>(() => {
    return {
      language_mode: threadOverrides.language_mode ?? accountSettings.language_mode,
      knowledge_scope: threadOverrides.knowledge_scope ?? accountSettings.knowledge_scope,
      tone_level: threadOverrides.tone_level ?? accountSettings.tone_level,
    }
  }, [threadOverrides, accountSettings])

  const settingsBadges = useMemo(
    () => [
      {
        label: 'Language',
        value: getLanguageModeLabel(effectiveSettings.language_mode),
        override: threadOverrides.language_mode !== null,
      },
      {
        label: 'Knowledge',
        value: getKnowledgeScopeLabel(effectiveSettings.knowledge_scope),
        override: threadOverrides.knowledge_scope !== null,
      },
      { label: 'Tone', value: getToneLevelLabel(effectiveSettings.tone_level), override: threadOverrides.tone_level !== null },
      {
        label: 'Commentary',
        value: quothaCommentaryEnabled ? 'On' : 'Off',
        override: threadInfo?.quotha_mode !== null && threadInfo?.quotha_mode !== undefined,
      },
    ],
    [effectiveSettings, threadOverrides, quothaCommentaryEnabled, threadInfo?.quotha_mode],
  )

  const effectiveQuothaMode = useMemo<QuothaMode>(() => {
    const normalized = normalizeQuothaMode(threadInfo?.quotha_mode ?? accountSettings.quotha_mode)
    return normalized === 'off' ? 'off' : 'on'
  }, [threadInfo?.quotha_mode, accountSettings.quotha_mode])

  useEffect(() => {
    setPendingSettings((prev) => ({
      ...prev,
      ...effectiveSettings,
      quotha_mode: effectiveQuothaMode,
      quotha_annotations: accountSettings.quotha_annotations ?? prev.quotha_annotations ?? false,
    }))
    setControlsSaved(false)
  }, [effectiveSettings, effectiveQuothaMode, accountSettings.quotha_annotations])

  useEffect(() => {
    setQuothaCommentaryEnabled(effectiveQuothaMode !== 'off')
  }, [effectiveQuothaMode])

  useEffect(() => {
    setQuothaPanels((prev) => {
      const next: typeof prev = {}
      Object.entries(prev).forEach(([id, entry]) => {
        if (!quothaCommentaryEnabled) {
          if (entry.source === 'auto') {
            next[id] = { ...entry, visible: false }
            return
          }
          next[id] = { ...entry }
          return
        }

        if (entry.source === 'auto') {
          next[id] = { ...entry, visible: entry.dismissed ? false : true }
          return
        }

        next[id] = { ...entry }
      })
      return next
    })
  }, [quothaCommentaryEnabled])

  const resolveFigureImage = (fig?: FigureLite | null) => {
    if (!fig) return undefined
    const candidate = fig.image_url || null
    const resolved = resolveMediaUrl(API_BASE, candidate)
    return normalizeImageSrc(resolved) ?? undefined
  }

  const currentFigureSlug = useMemo(() => {
    const raw = figure?.slug ?? threadInfo?.figure_slug
    if (typeof raw !== 'string') return null
    const trimmed = raw.trim()
    return trimmed.length ? trimmed : null
  }, [figure?.slug, threadInfo?.figure_slug])

  const metaLine = useMemo(() => {
    const parts: string[] = []
    const date = threadInfo?.updated_at || threadInfo?.created_at
    if (date) {
      const delta = Date.now() - new Date(date).getTime()
      const minutes = Math.max(0, Math.round(delta / (1000 * 60)))
      if (minutes < 1) parts.push('Last active just now')
      else if (minutes < 60) parts.push(`Last active ${minutes}m ago`)
      else {
        const hours = Math.round(minutes / 60)
        if (hours < 24) parts.push(`Last active ${hours}h ago`)
        else {
          const days = Math.round(hours / 24)
          parts.push(`Last active ${days}d ago`)
        }
      }
    }
    parts.push(getLanguageModeLabel(effectiveSettings.language_mode))
    parts.push(getKnowledgeScopeLabel(effectiveSettings.knowledge_scope))
    return parts.join(' · ')
  }, [threadInfo, effectiveSettings])

  const activeLedgerVersion = useMemo(() => {
    return [...messages]
      .reverse()
      .find((msg) => msg.role === 'assistant' && msg.epistemic?.activeRagVersion)?.epistemic?.activeRagVersion
  }, [messages])

  useEffect(() => {
    if (!ready) return

    if (!isAuthenticated || !token) {
      const next = threadId ? `/chat/${threadId}` : '/chat'
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true })
    }
  }, [ready, isAuthenticated, token, threadId, navigate])

  useEffect(() => {
    const stored = readSourcesVisibility(threadStorageKey)
    sourcesVisibilityRef.current = stored
    setSourcesHidden({ ...stored })
  }, [threadStorageKey])

  useEffect(() => {
    let cancelled = false
    loadFigureSlugRegistry().catch(() => {
      if (!cancelled && import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[CHAT_THREAD] Could not preload figure slug registry')
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!ready || !token || !threadId) return

    let cancelled = false
    hydratingHistoryRef.current = true
    pendingHistoryRenderRef.current = true
    setThreadLoading(true)
    setThreadError(null)
    setHistoryNotice(null)
    setFigure(null)
    setMessages([])
    setQuothaPanels({})
    animatedAssistantMessageIdsRef.current.clear()
    animatedQuothaPanelIdsRef.current.clear()

    const hydrateThread = async () => {
      let historyHydrated = false
      try {
        const thread = await api.getThread(threadId, token)
        if (cancelled) return
        setThreadInfo(normalizeThread(thread))
        const threadSlug = thread.figure_slug
        try {
          const isValidSlug = await ensureValidFigureSlug(threadSlug)
          if (cancelled) return
          if (isValidSlug && threadSlug) {
            const fig = await api.getFigureLite(threadSlug)
            if (!cancelled) setFigure(fig)
          } else if (threadSlug) {
            setSlugValidationError(OUT_OF_DATE_FIGURE_MESSAGE)
          }
        } catch {
          if (!cancelled && import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn('[CHAT_THREAD] Figure validation failed before loading profile')
          }
        }
        try {
          const history = await api.listThreadMessages(threadId, token)
          if (!cancelled && Array.isArray(history)) {
            const mapped = history
              .map((msg, index) => mapThreadMessage(msg, index))
              .filter((msg): msg is ChatMessage => Boolean(msg))
            seedAssistantMessages(mapped)
            seedQuothaPanels(mapped)
            pendingHistoryRenderRef.current = false
            setMessages(mapped)
            historyHydrated = true
          }
        } catch (err) {
          if (cancelled) return
          if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
            setHistoryNotice('History is unavailable (missing backend endpoint)')
          }
        }
      } catch (err) {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 404) {
          setThreadError('This conversation no longer exists.')
        } else {
          const message = err instanceof ApiError ? err.message : 'Unable to load this thread.'
          setThreadError(message)
        }
      } finally {
        if (!cancelled) setThreadLoading(false)
        if (!historyHydrated && !cancelled) {
          pendingHistoryRenderRef.current = false
          hydratingHistoryRef.current = false
        }
      }
    }

    const loadSettings = async () => {
      try {
        const data = await api.getUserSettings(token)
        if (cancelled) return
        const { values } = mergeSettings(data)
        setAccountSettings(values)
      } catch (err) {
        if (cancelled) return
        const message = err instanceof ApiError ? err.message : 'Unable to load your chat settings.'
        setSettingsError(message)
      }
    }

    void hydrateThread()
    void loadSettings()

    return () => {
      cancelled = true
    }
  }, [ready, token, threadId, seedAssistantMessages, seedQuothaPanels])

  useEffect(() => {
    if (!pendingHistoryRenderRef.current && hydratingHistoryRef.current) {
      hydratingHistoryRef.current = false
    }
  }, [messages])

  useEffect(() => {
    if (!threadStorageKey) return
    if (sourcesHidden === sourcesVisibilityRef.current) {
      writeSourcesVisibility(threadStorageKey, sourcesHidden)
      return
    }
    sourcesVisibilityRef.current = { ...sourcesHidden }
    writeSourcesVisibility(threadStorageKey, sourcesVisibilityRef.current)
  }, [sourcesHidden, threadStorageKey])

  useEffect(() => {
    scrollToBottom('smooth')
  }, [messages, sending, scrollToBottom])

  useEffect(() => {
    setQuothaPanels((prev) => {
      let changed = false
      const next = { ...prev }
      messages.forEach((msg) => {
        if (msg.role !== 'assistant') return
        const panel = msg.quotha_panel ?? msg.quotha?.panel ?? null
        const status = msg.quotha_status ?? normalizeQuothaStatus(panel ? 'ready' : 'none')
        const shouldTrack = status === 'pending' || panel !== null
        if (!shouldTrack) return

        if (!next[msg.id]) {
          const wantsVisible = quothaCommentaryEnabled && (status === 'pending' || status === 'ready')
          next[msg.id] = { panel, visible: wantsVisible, source: 'auto', dismissed: !wantsVisible }
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [messages, quothaCommentaryEnabled])

  useEffect(() => {
    setSourcesHidden((prev) => {
      let changed = false
      let refChanged = false
      const refNext = { ...sourcesVisibilityRef.current }
      const next = { ...prev }
      messages.forEach((msg, index) => {
        if (msg.role !== 'assistant') return
        const storageKey = getMessageStorageKey(msg, index)
        if (next[storageKey] !== undefined) return
        const { sourcesCount } = getMessageSourcesMeta(msg)
        const worksPresent = uniqueSorted(getMessageWorksPresent(msg))
        const hasSourcesPanel = sourcesCount > 0 || (Array.isArray(msg.sources) && msg.sources.length > 0)
        const hasContextPanelContent = hasSourcesPanel || worksPresent.length > 0 || Boolean(msg.epistemic?.retrievalSimilarityBand)
        if (hasContextPanelContent) {
          const storedHidden = sourcesVisibilityRef.current[storageKey]
          next[storageKey] = storedHidden !== undefined ? storedHidden : true
          if (storedHidden === undefined) {
            refNext[storageKey] = true
            refChanged = true
          }
          changed = true
        }
      })
      if (refChanged) {
        sourcesVisibilityRef.current = refNext
      }
      return changed ? next : prev
    })
  }, [messages])

  useEffect(() => {
    const slug = threadInfo?.figure_slug ?? null
    if (!slug) {
      setSlugValidationError(null)
      return
    }

    let cancelled = false

    loadFigureSlugRegistry()
      .then((registry) => {
        if (cancelled) return
        const isValid = registry.has(slug)
        setSlugValidationError(isValid ? null : OUT_OF_DATE_FIGURE_MESSAGE)
        if (!isValid) {
          // eslint-disable-next-line no-console
          console.warn('[CHAT_THREAD] Thread references unknown figure slug', {
            slug,
            registrySize: registry.size,
          })
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSlugValidationError(null)
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn('[CHAT_THREAD] Unable to validate figure slug for thread')
          }
        }
      })

    return () => {
      cancelled = true
    }
  }, [threadInfo?.figure_slug])

  useEffect(() => {
    if (figure) {
      primeFigureSlugRegistry([figure])
    }
  }, [figure])

  const setPanelForMessage = (messageId: string, panel: QuothaPanelPayload | null, visible: boolean, source: 'auto' | 'manual') => {
    setQuothaPanels((prev) => ({
      ...prev,
      [messageId]: { panel, visible, source, dismissed: false },
    }))
  }

  const updatePanelVisibility = (messageId: string, visible: boolean) => {
    setQuothaPanels((prev) => {
      if (!prev[messageId]) return prev
      return { ...prev, [messageId]: { ...prev[messageId], visible, dismissed: visible ? false : true } }
    })
  }

  const revealPanelManually = (messageId: string) => {
    setQuothaPanels((prev) => {
      if (!prev[messageId]) return prev
      return { ...prev, [messageId]: { ...prev[messageId], visible: true, source: 'manual', dismissed: false } }
    })
  }

  const updateSourcesVisibility = (storageKey: string, hidden: boolean) => {
    setSourcesHidden((prev) => {
      if (prev[storageKey] === hidden) {
        return prev
      }
      const next = {
        ...prev,
        [storageKey]: hidden,
      }
      persistSourcesVisibility(storageKey, hidden)
      return next
    })
  }

  const closeSnippetModal = useCallback(() => {
    snippetRequestIdRef.current += 1
    setSnippetModal(null)
  }, [])

  const openSnippetModal = useCallback(async (source: NormalizedSource) => {
    const requestId = snippetRequestIdRef.current + 1
    snippetRequestIdRef.current = requestId
    const fallbackText = getSourceModalText(source)
    const fallbackTitle = getSourceDisplayTitle(source)
    const fallbackByline = getSourceByline(source)
    const fallbackUrl = resolveSourceText(source.url) ?? undefined
    const sourceType = getSourceTypeLabel(source)
    const partLabel = getSourcePartLabel(source)
    const hasFallbackText = Boolean(fallbackText.excerptText || fallbackText.fullChunkText)

    setSnippetModal({
      source,
      sourceTitle: fallbackTitle,
      sourceByline: fallbackByline,
      sourceUrl: fallbackUrl,
      sourceType,
      partLabel,
      publicationDate: source.publicationDate,
      activeVersion: source.activeVersion,
      excerptText: fallbackText.excerptText,
      fullChunkText: fallbackText.fullChunkText,
      loading: !hasFallbackText,
    })

    if (!source.chunkId || !currentFigureSlug || !token) {
      setSnippetModal((prev) => (prev ? { ...prev, loading: false, error: hasFallbackText ? undefined : 'Passage unavailable.' } : prev))
      return
    }

    try {
      const response = await api.getContextSnippet(
        { figureSlug: currentFigureSlug, chunkId: source.chunkId },
        token,
      )

      if (snippetRequestIdRef.current !== requestId) return

      const responseText = trimLeadingBrokenFragment(
        coerceSourceText(response.chunk_text ?? response.content ?? response.snippet ?? response.text) ?? undefined,
      )
      const excerptText = fallbackText.excerptText ?? responseText
      const fullChunkText =
        fallbackText.fullChunkText ??
        (responseText && responseText !== excerptText ? responseText : undefined)
      const sourceTitle =
        normalizeDisplayString(response.source_title) ?? fallbackTitle
      const publicationDate = coerceSourceText(response.source_pub_date ?? response.publication_date) ?? source.publicationDate
      const activeVersion = coerceSourceText(response.active_rag_version) ?? source.activeVersion

      setSnippetModal((prev) => {
        if (!prev) return prev
        if (!excerptText && !fullChunkText) {
          return { ...prev, sourceTitle, publicationDate, activeVersion, loading: false, error: 'Passage unavailable.' }
        }
        return {
          ...prev,
          sourceTitle,
          publicationDate,
          activeVersion,
          excerptText,
          fullChunkText,
          loading: false,
          error: undefined,
        }
      })
    } catch {
      if (snippetRequestIdRef.current !== requestId) return
      setSnippetModal((prev) => (
        prev
          ? { ...prev, loading: false, error: hasFallbackText ? undefined : 'Passage unavailable.' }
          : prev
      ))
    }
  }, [currentFigureSlug, token])

  useEffect(() => {
    if (!snippetModal) return undefined
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSnippetModal()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [snippetModal, closeSnippetModal])

  useEffect(() => {
    const node = scrollContainerRef.current
    if (!node) return undefined

    const handleScroll = () => {
      const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight
      pinnedToBottomRef.current = distanceFromBottom < 64
    }

    node.addEventListener('scroll', handleScroll)
    return () => {
      node.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const node = scrollContainerRef.current
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(() => {
      if (pinnedToBottomRef.current) {
        scrollToBottom('auto', true)
      }
    })

    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [scrollToBottom])

  const patchMessageQuotha = (
    messageId: string,
    updates: Partial<{
      quotha_status: QuothaStatus
      quotha_panel: QuothaPanelPayload | null
      quotha: QuothaBundle | null
      quotha_error_ui: string | null
      quotha_flags: unknown
    }>,
  ) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId) return msg

        const nextFlags = Array.isArray((updates.quotha as QuothaBundle | null)?.flags)
          ? (updates.quotha as QuothaBundle).flags
          : Array.isArray(updates.quotha_flags)
            ? (updates.quotha_flags as QuothaBundle['flags'])
            : msg.quotha?.flags ?? []

        const nextPanel =
          (updates.quotha_panel ?? (updates.quotha as QuothaBundle | null)?.panel ?? msg.quotha_panel ?? msg.quotha?.panel) ?? null

        const nextStatus = normalizeQuothaStatus(
          updates.quotha_status ?? (updates.quotha as QuothaBundle | null)?.status ?? msg.quotha_status ?? msg.quotha?.status,
        )

        const nextQuotha: QuothaBundle = {
          flags: nextFlags,
          panel: nextPanel,
          status: nextStatus,
        }

        return {
          ...msg,
          quotha_status: nextStatus,
          quotha_panel: nextPanel,
          quotha: nextQuotha,
          quotha_error_ui: updates.quotha_error_ui ?? msg.quotha_error_ui ?? null,
        }
      }),
    )
  }

  const pollQuotha = useCallback(async (messageId: string) => {
    if (!threadId || !token) return
    if (pollingMessagesRef.current.has(messageId)) return

    pollingMessagesRef.current.add(messageId)
    const start = Date.now()
    const backoff = [400, 800, 1500, 2500]
    let backoffIndex = 0

    try {
      while (Date.now() - start < 20000) {
        let response: Awaited<ReturnType<typeof api.getThreadMessageQuotha>> | null = null

        try {
          response = await api.getThreadMessageQuotha(threadId, messageId, token)
        } catch (err) {
          patchMessageQuotha(messageId, { quotha_status: 'failed', quotha_error_ui: 'Failed to load commentary.' })
          break
        }

        const panel = normalizeQuothaPanelPayload(response?.quotha_panel ?? response?.quotha?.panel)
        const flags = Array.isArray(response?.quotha?.flags)
          ? response?.quotha?.flags
          : Array.isArray(response?.quotha_flags)
            ? response?.quotha_flags
            : []
        const status = normalizeQuothaStatus(response?.status ?? response?.quotha?.status ?? response?.quotha_status)

        if (status === 'ready') {
          patchMessageQuotha(messageId, {
            quotha_status: 'ready',
            quotha_panel: panel,
            quotha: { flags, panel, status: 'ready' },
            quotha_error_ui: null,
          })
          if (panel) {
            setPanelForMessage(messageId, panel, quothaCommentaryEnabled, 'auto')
          }
          break
        }

        if (status === 'failed' || status === 'none') {
          patchMessageQuotha(messageId, {
            quotha_status: status,
            quotha_panel: panel ?? null,
            quotha: { flags, panel: panel ?? null, status },
            quotha_error_ui: status === 'failed' ? 'Failed to load commentary.' : null,
          })
          break
        }

        const delay = backoff[Math.min(backoffIndex, backoff.length - 1)]
        backoffIndex = Math.min(backoffIndex + 1, backoff.length - 1)
        await new Promise((resolve) => setTimeout(resolve, delay))
      }

      if (Date.now() - start >= 20000) {
        patchMessageQuotha(messageId, { quotha_status: 'failed', quotha_error_ui: 'Failed to load commentary.' })
      }
    } finally {
      pollingMessagesRef.current.delete(messageId)
    }
  }, [threadId, token, quothaCommentaryEnabled])

  useEffect(() => {
    if (!threadId || !token) return
    const pending = messages.filter((msg) => msg.role === 'assistant' && msg.quotha_status === 'pending')
    pending.slice(-2).forEach((msg) => {
      void pollQuotha(msg.id)
    })
  }, [messages, threadId, token, pollQuotha])

  const sendMessage = async (options?: { quotha?: boolean }) => {
    const message = inputValue.trim()

    if (!ready || !token) {
      setError('You need to be logged in before sending messages.')
      return
    }

    if (!message || !threadId || sendingRef.current) {
      return
    }

    if (slugValidationError) {
      setError(slugValidationError)
      return
    }

    sendingRef.current = true
    setSending(true)
    setError(null)
    setQuothaPanels((prev) => prev)

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      created_at: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, userMessage])
    setInputValue('')

    const wantsQuotha = quothaCommentaryEnabled || Boolean(options?.quotha)

    const payload = {
      message,
      quotha_mode: toQuothaMode(wantsQuotha),
      ...(wantsQuotha ? { quotha: true } : {}),
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug('[CHAT_SEND]', {
        effectiveSettings,
        draftSettings: pendingSettings,
        payload,
      })
    }

    try {
      const response = await api.askThread(threadId, payload, token)
      const answer = response?.answer ?? response?.message ?? ''
      const rawPanel = response?.quotha_panel ?? response?.quotha?.panel
      const panel = normalizeQuothaPanelPayload(rawPanel)
      const flags = Array.isArray(response?.quotha?.flags)
        ? response?.quotha?.flags ?? []
        : Array.isArray(response?.quotha_flags)
          ? response?.quotha_flags
          : []
      const rawSources = Array.isArray(response?.sources) ? response.sources : []
      const sources = normalizeSources(rawSources)
      const answerMeta = response?.answer_meta
      const responseSourcesAvailable = answerMeta?.sources_available ?? response?.sources_available ?? false
      const responseSourcesCount =
        typeof answerMeta?.sources_count === 'number'
          ? Math.max(0, answerMeta.sources_count)
          : typeof response?.sources_count === 'number'
            ? Math.max(0, response.sources_count)
            : sources.length
      const responseWorksPresent = normalizeWorksPresent(answerMeta?.works_present ?? response?.works_present)
      const responseEpistemic = normalizeEpistemic(response?.epistemic)
      const quothaStatus = normalizeQuothaStatus(
        response?.quotha?.status ?? response?.quotha_status ?? (panel ? 'ready' : 'none'),
      )
      const assistantId = response?.assistant_message_id ?? `assistant-${Date.now()}`
      const assistantMessage: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: answer || 'No answer returned.',
        created_at: new Date().toISOString(),
        quotha_panel: panel,
        quotha: {
          flags,
          panel,
          status: quothaStatus,
        },
        quotha_status: quothaStatus,
        sources,
        rawSources,
        answer_meta: answerMeta,
        sources_available: responseSourcesAvailable,
        sources_count: responseSourcesCount,
        works_present: responseWorksPresent.length ? responseWorksPresent : undefined,
        epistemic: responseEpistemic,
      }
      setMessages((prev) => [...prev, assistantMessage])

      const hasContextPanelContent =
        (responseSourcesAvailable && responseSourcesCount > 0) ||
        responseWorksPresent.length > 0 ||
        Boolean(responseEpistemic?.retrievalSimilarityBand)
      if (hasContextPanelContent) {
        setSourcesHidden((prev) => ({
          ...prev,
          [assistantId]: true,
        }))
      }

      if (rawPanel && !panel && import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('[QUOTHA] Dropped quotha_panel payload from ask response', rawPanel)
      } else if (rawPanel && !assistantMessage.quotha_panel && import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('[QUOTHA] quotha_panel present in response but missing on message state', rawPanel)
      }

      if (panel) {
        setPanelForMessage(assistantId, panel, quothaCommentaryEnabled, 'auto')
      }

      if (quothaStatus === 'pending') {
        void pollQuotha(assistantId)
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Session expired. Log in again, then press Send to retry.')
        setInputValue(message)
      } else {
        const friendlyMessage = err instanceof ApiError ? err.message : 'Something went wrong sending your message.'
        setError(friendlyMessage)
      }
    } finally {
      sendingRef.current = false
      setSending(false)
    }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    void sendMessage()
  }

  const handleQuothaToggle = (next: boolean) => {
    setQuothaCommentaryEnabled(next)
    setPendingSettings((prev) => ({ ...prev, quotha_mode: toQuothaMode(next) }))
    setControlsSaved(false)
    setControlsError(null)

    if (!ready || !token || !threadId) return

    const desiredMode = toQuothaMode(next)
    void (async () => {
      try {
        const updated = await api.updateThreadSettings(threadId, { quotha_mode: desiredMode }, token)
        setThreadInfo(normalizeThread(updated))
        setControlsSaved(true)
      } catch (err) {
        const fallbackMode = normalizeQuothaMode(threadInfo?.quotha_mode ?? accountSettings.quotha_mode)
        const fallbackEnabled = resolveQuothaEnabled(fallbackMode)
        setControlsError(err instanceof ApiError ? err.message : 'Unable to update Quotha setting right now.')
        setPendingSettings((prev) => ({ ...prev, quotha_mode: fallbackMode }))
        setQuothaCommentaryEnabled(fallbackEnabled)
      }
    })()
  }

  const handleControlChange = <K extends keyof SettingsValues>(key: K, value: SettingsValues[K]) => {
    setPendingSettings((prev) => ({ ...prev, [key]: value }))
    if (key === 'quotha_mode') {
      setQuothaCommentaryEnabled(resolveQuothaEnabled(value as Thread['quotha_mode']))
    }
    setControlsSaved(false)
    setControlsError(null)
  }

  const handleSaveControls = async () => {
    if (!ready || !token || !threadId) return
    setControlsSaving(true)
    setControlsError(null)
    setControlsSaved(false)

    const payload: ThreadSettingsPayload = {}
    ;(['language_mode', 'knowledge_scope', 'tone_level'] as const).forEach((key) => {
      const baseValue = accountSettings[key]
      const nextValue = pendingSettings[key]

      if (key === 'language_mode') {
        const existingOverride = threadInfo?.language_mode_override
        if (nextValue !== baseValue) payload.language_mode_override = nextValue
        else if (existingOverride !== null && existingOverride !== undefined) payload.language_mode_override = null
        return
      }

      if (key === 'knowledge_scope') {
        const existingOverride = threadInfo?.knowledge_scope_override
        if (nextValue !== baseValue) payload.knowledge_scope_override = nextValue
        else if (existingOverride !== null && existingOverride !== undefined) payload.knowledge_scope_override = null
        return
      }

      const existingOverride = threadInfo?.tone_level_override
      if (nextValue !== baseValue) payload.tone_level_override = nextValue
      else if (existingOverride !== null && existingOverride !== undefined) payload.tone_level_override = null
    })

    const currentQuothaMode = toQuothaMode(resolveQuothaEnabled(threadInfo?.quotha_mode))
    const nextQuothaMode = toQuothaMode(resolveQuothaEnabled(pendingSettings.quotha_mode))
    if (nextQuothaMode !== currentQuothaMode) {
      payload.quotha_mode = nextQuothaMode
    }

    if (!Object.keys(payload).length) {
      setControlsSaved(true)
      setControlsSaving(false)
      return
    }

    try {
      const updated = await api.updateThreadSettings(threadId, payload, token)
      setThreadInfo(normalizeThread(updated))
      setControlsSaved(true)
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setControlsError('Session expired. Log in again, then save to apply these thread controls.')
      } else {
        const message = err instanceof ApiError ? err.message : 'Unable to save your settings right now.'
        setControlsError(message)
      }
    } finally {
      setControlsSaving(false)
    }
  }

  if (!ready) {
    return (
      <section className="chat-page">
        <p>Preparing your chat...</p>
      </section>
    )
  }

  if (!isAuthenticated || !token) {
    return (
      <section className="chat-page">
        <p className="chat-error">You need to be logged in to view this chat.</p>
      </section>
    )
  }

  if (!threadId) {
    return (
      <section className="chat-page">
        <p className="chat-error">No thread selected. Return to the dashboard to pick a conversation.</p>
      </section>
    )
  }

  const disabled = sending || !threadId || Boolean(slugValidationError)
  const assistantLabel = threadInfo?.figure_name ?? figure?.name ?? threadInfo?.title ?? 'Assistant'
  const figureImageSrc = resolveFigureImage(figure)
  const figureSlug = currentFigureSlug ?? 'unknown'
  const thumbKey = `${threadId ?? 'thread'}:${figureSlug}:${figureImageSrc ?? 'missing'}`

  const anyThreadOverrides = settingsBadges.some((badge) => badge.override)
  const conversationSettingsSummary = settingsBadges
    .map((badge) => (badge.label === 'Commentary' ? `Integrity Audit ${badge.value}` : badge.value))
    .join(' · ')

  return (
    <section className="chat-page">
      <div className="chat-actions-bar">
        <button type="button" className="button ghost" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
        <button type="button" className="button" onClick={() => navigate('/settings')}>
          Settings
        </button>
      </div>
      <div className="chat-hero">
        {threadLoading && (
          <div className="hero-thumb-skeleton" aria-hidden />
        )}
        {!threadLoading && (
          <SafeFigureThumb
            rawSrc={figureImageSrc}
            alt={figure?.name ?? 'Figure portrait'}
            size={64}
            variant="circle"
            fallbackText={figure?.name ?? threadInfo?.figure_slug ?? 'PiT'}
            cacheKey={thumbKey}
          />
        )}
        <div className="chat-hero-text">
          <p className="eyebrow">Conversation</p>
          <h1 className="clamp-1">
            {figure?.name ?? threadInfo?.title ?? `Thread ${threadId}`}
            {activeLedgerVersion && <span className="ledger-badge">Ledger: {activeLedgerVersion}</span>}
          </h1>
          <p className="chat-hero-meta clamp-1">{metaLine}</p>
          <SourceDepthBadge depth={figure?.source_depth} variant="compact" />
          <p className="chat-hero-snippet clamp-2">
            {threadInfo?.last_message || 'Ask a question to continue the conversation.'}
          </p>
        </div>
      </div>
      <p className="chat-limit-note">Controls below affect this thread only. Account defaults stay unchanged.</p>
      <p className="chat-epistemic-microcopy">LLM for fluency. Epistemic control via versioned retrieval.</p>
      {slugValidationError && (
        <div className="inline-error-card" role="alert">
          <div>
            <p className="error-title">Figure needs refresh</p>
            <p className="error-state">{slugValidationError}</p>
            <p className="hint">This thread points to a figure slug the site no longer recognizes. Start a new chat to continue.</p>
          </div>
          <div className="hero-actions">
            <button type="button" className="button primary" onClick={() => navigate('/dashboard')}>
              Start new chat
            </button>
            <button type="button" className="button ghost" onClick={() => navigate('/people')}>
              Choose a figure
            </button>
          </div>
        </div>
      )}
      {settingsError && <p className="chat-error">{settingsError}</p>}
      {historyNotice && <p className="chat-error">{historyNotice}</p>}
      {!settingsError && (
        <div className="chat-settings-summary" role="note" aria-label="Conversation settings">
          <span className="chat-settings-summary-text">
            Conversation settings: {conversationSettingsSummary}
            {anyThreadOverrides ? <span className="chat-settings-summary-hint"> · Thread overrides</span> : null}
          </span>
          <button type="button" className="button ghost" onClick={toggleControlsExpanded}>
            {controlsExpanded ? 'Hide' : 'Adjust'}
          </button>
        </div>
      )}
      <div className="chat-layout">
        <div className="chat-main-column">
          {error && <p className="chat-error">{error}</p>}
          {threadError && <p className="chat-error">{threadError}</p>}

          <div className="chat-shell">
            <div className="chat-messages" ref={scrollContainerRef}>
              {messages.length === 0 && (
                <p className="chat-empty">Start with a question.</p>
              )}
              {messages.map((msg, index) => {
                const messageId = msg.id ?? `msg-${index}`
                const storageKey = getMessageStorageKey(msg, index)
                const isAssistant = msg.role === 'assistant'
                const quothaStatus = isAssistant ? msg.quotha_status ?? 'none' : 'none'
                const panelEntry = msg.role === 'assistant' ? quothaPanels[msg.id] : undefined
                const commentaryAllowed = quothaCommentaryEnabled === true
                const allowManualPanel = Boolean(panelEntry?.source === 'manual')
                const panelVisible =
                  isAssistant && Boolean(panelEntry?.visible) && (commentaryAllowed || allowManualPanel)
                const showInlineRevealBase = isAssistant && Boolean(panelEntry && !panelEntry.visible) && (commentaryAllowed || allowManualPanel)
                const showPendingBase = panelVisible && quothaStatus === 'pending'
                const showFailedBase = panelVisible && quothaStatus === 'failed'
                const showPanelBase = panelVisible && quothaStatus === 'ready' && Boolean(panelEntry?.panel)
                const quothaFlags = isAssistant ? (msg.quotha?.flags ?? []) : []
                const contextMatchBand = isAssistant ? msg.epistemic?.retrievalSimilarityBand : undefined
                const uncertaintyFlags = isAssistant ? (msg.epistemic?.uncertaintyFlags ?? []) : []
                const hasUncertainty = uncertaintyFlags.length > 0
                const sources = isAssistant ? msg.sources ?? [] : []
                const rawSources = isAssistant ? msg.rawSources ?? [] : []
                const worksPresent = isAssistant ? uniqueSorted(getMessageWorksPresent(msg)) : []
                const { sourcesCount } = isAssistant
                  ? getMessageSourcesMeta(msg)
                  : { sourcesCount: 0 }
                const hasSourcesPanel = sourcesCount > 0 || sources.length > 0
                const hasWorks = worksPresent.length > 0
                const hasContextPanelContent = hasSourcesPanel || hasWorks || Boolean(contextMatchBand)
                const isSourcesHidden = isAssistant && Boolean(sourcesHidden[storageKey])
                const contextNoteLabel =
                  sourcesCount > 0
                    ? `Context & sources available (${sourcesCount} hidden)`
                    : 'Context & sources available (hidden)'
                const shouldWarnMissingSources = sourcesCount > 0 && sources.length === 0

                if (shouldWarnMissingSources && !missingSourcesWarnings.has(msg.id)) {
                  missingSourcesWarnings.add(msg.id)
                  if (import.meta.env.DEV) {
                    // eslint-disable-next-line no-console
                    console.warn('[CHAT_THREAD] Sources available but could not be displayed', {
                      messageId: msg.id,
                      sourcesCount,
                      rawSources,
                      normalizedSources: sources,
                    })
                  }
                }

                const assistantAlreadyAnimated = animatedAssistantMessageIdsRef.current.has(messageId)
                const shouldAnimateAssistant =
                  isAssistant &&
                  typingAnimationEnabled &&
                  !typingAnimationBlocked &&
                  !assistantAlreadyAnimated &&
                  !hydratingHistoryRef.current
                const assistantFullyRendered = assistantAlreadyAnimated || !shouldAnimateAssistant
                const contextReady = isAssistant && assistantFullyRendered
                const showContextPanel = contextReady && hasContextPanelContent && !isSourcesHidden
                const showContextNote = contextReady && hasContextPanelContent && isSourcesHidden 

                if (isAssistant && assistantFullyRendered && !assistantAlreadyAnimated) {
                  registerAssistantMessage(messageId)
                }

                const quothaAlreadyAnimated = animatedQuothaPanelIdsRef.current.has(messageId)
                const quothaDisplayReady = assistantFullyRendered
                const showInlineReveal = quothaDisplayReady && showInlineRevealBase
                const showPending = quothaDisplayReady && showPendingBase
                const showFailed = quothaDisplayReady && showFailedBase
                const showPanel = quothaDisplayReady && showPanelBase
                const shouldAnimateQuothaPanel =
                  isAssistant &&
                  showPanel &&
                  typingAnimationEnabled &&
                  !typingAnimationBlocked &&
                  !quothaAlreadyAnimated
                if (showPanel && panelEntry?.panel && !shouldAnimateQuothaPanel && !quothaAlreadyAnimated) {
                  animatedQuothaPanelIdsRef.current.add(messageId)
                }

                return (
                  <div
                    key={messageId}
                    className={`chat-message chat-message-${msg.role === 'user' ? 'user' : 'assistant'}${hasUncertainty ? ' has-uncertainty' : ''}`}
                  >
                    <div className="chat-message-meta">
                      {hasUncertainty && (
                        <span className="chat-raven-indicator" title={uncertaintyFlags.join(' | ') || 'Uncertainty flagged'}>
                          <img src="/assets/quotha/quotha_head_l.png" alt="Uncertainty flagged" />
                        </span>
                      )}
                      <span className="chat-message-role">{msg.role === 'user' ? 'YOU' : assistantLabel}</span>
                    </div>
                    <div className="chat-message-content">
                      {isAssistant ? (
                        <TypewriterReveal
                          text={msg.content}
                          enabled={shouldAnimateAssistant}
                          className="typewriter-block"
                          onDone={() => {
                            markAssistantAnimationComplete(messageId)
                            scrollToBottom('auto')
                          }}
                        />
                      ) : (
                        msg.content
                      )}
                    </div>

                    {quothaFlags.length > 0 && (
                      <div className="quotha-flags" role="status" aria-live="polite">
                        {quothaFlags.map((flag, index) => (
                          <span
                            key={`${flag.code ?? 'flag'}-${index}`}
                            className={`quotha-flag ${flag.severity ?? 'info'}`}
                          >
                            {flag.message}
                          </span>
                        ))}
                      </div>
                    )}

                    {showContextNote && (
                      <button
                        type="button"
                        className="context-note"
                        onClick={() => updateSourcesVisibility(storageKey, false)}
                      >
                        {contextNoteLabel}
                      </button>
                    )}

                    {showContextPanel && (
                      <div className="context-panel">
                        <div className="context-panel-header">
                          <div className="context-panel-title-row">
                            <span className="context-panel-title">Context & sources</span>
                            {contextMatchBand && <ContextMatchBadge band={contextMatchBand} />}
                          </div>
                          <button
                            type="button"
                            className="context-panel-hide"
                            onClick={() => updateSourcesVisibility(storageKey, true)}
                          >
                            Hide
                          </button>
                        </div>
                        {sources.length > 0 && (
                          <ul className="context-sources-list">
                            {sources.map((source, index) => {
                              const label = getSourceDisplayTitle(source)
                              const typeLabel = getSourceTypeLabel(source)
                              const passageLabel = getSourcePartLabel(source)
                              const key = `${label}-${source.url ?? 'no-url'}-${source.chunkId ?? 'no-chunk'}-${index}`
                              return (
                                <li key={key}>
                                  <div className="context-source-heading">
                                    <span className="context-source-label">{label}</span>
                                    <button type="button" className="context-source-trigger" onClick={() => void openSnippetModal(source)}>
                                      View evidence
                                    </button>
                                  </div>
                                  {(source.publicationDate || typeLabel || passageLabel) && (
                                    <div className="context-source-meta-row">
                                      {source.publicationDate && <span>{source.publicationDate}</span>}
                                      {typeLabel && <span>{typeLabel}</span>}
                                      {passageLabel && <span>{passageLabel}</span>}
                                    </div>
                                  )}
                                </li>
                              )
                            })}
                          </ul>
                        )}
                        {sourcesCount > 0 && sources.length === 0 && (
                          <p className="context-sources-empty">
                            Sources are available, but couldn't be displayed.
                          </p>
                        )}
                        {worksPresent.length > 0 && (
                          <div className="context-works">
                            <p className="context-works-title">Works referenced</p>
                            <div className="context-works-chips">
                              {worksPresent.map((work, index) => (
                                <span key={`${work}-${index}`} className="context-work-chip">
                                  {work}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {showInlineReveal && (
                      <button
                        type="button"
                        className="quotha-inline-link"
                        onClick={() => revealPanelManually(msg.id)}
                      >
                        Why this stays historical
                      </button>
                    )}

                    {showPending && (
                      <div className="quotha-panel pending" role="status" aria-live="polite">
                        <div className="quotha-panel-main">
                          <div className="quotha-panel-copy">
                            <p className="quotha-panel-body clamp-1">Checking context…</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {showFailed && (
                      <div className="quotha-panel failed" role="status" aria-live="polite">
                        <div className="quotha-panel-main">
                          <div className="quotha-panel-copy">
                            <p className="quotha-panel-body clamp-2">{msg.quotha_error_ui ?? 'Quotha failed to load.'}</p>
                            <button type="button" className="quotha-inline-link" onClick={() => pollQuotha(msg.id)}>
                              Try again
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {showPanel && panelEntry && (
                      <QuothaPanel
                        panel={panelEntry.panel}
                        variant="attached"
                        onDismiss={() => updatePanelVisibility(msg.id, false)}
                        animateBody={shouldAnimateQuothaPanel}
                        onAnimateComplete={() => {
                          animatedQuothaPanelIdsRef.current.add(messageId)
                          scrollToBottom('auto')
                        }}
                      />
                    )}
                  </div>
                )
              })}
              {sending && (
                <div className="chat-message chat-message-assistant typing">
                  <div className="chat-message-meta">
                    <span className="chat-message-role">{assistantLabel}</span>
                  </div>
                  <div className="chat-message-content">
                    <div className="chat-typing-indicator" role="status" aria-live="polite">
                      <span className="chat-typing-label">{assistantLabel} is thinking...</span>
                      <span className="chat-typing-dots" aria-hidden="true">
                        <span className="chat-typing-dot" />
                        <span className="chat-typing-dot" />
                        <span className="chat-typing-dot" />
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-composer">
              <form className="chat-input-row" onSubmit={handleSubmit}>
                <input
                  type="text"
                  className="chat-input"
                  placeholder="Ask your question..."
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  disabled={disabled}
                />
                <button type="submit" className="button primary" disabled={disabled}>
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </form>
            </div>
          </div>
        </div>

        <aside className="chat-meta-column">
          <div className="thread-controls-panel">
            <div className="thread-controls-header">
              <div className="thread-controls-copy">
                <p className="eyebrow strong">Conversation settings</p>
                <p className="hint">This thread only.</p>
              </div>
            </div>

            <div className="thread-controls-status">
              <span className={`quotha-pill minimal${quothaCommentaryEnabled ? ' on' : ''}`}>
                Integrity Audit {quothaCommentaryEnabled ? 'On' : 'Off'}
              </span>
              {controlsSaved && <span className="control-saved">Saved</span>}
              {controlsSaving && <span className="control-saving">Saving…</span>}
            </div>

            {controlsError && <p className="error-state thread-controls-error">{controlsError}</p>}

            {controlsExpanded && (
              <div className="thread-controls-body">
                <div className="quotha-toggle-card">
                  <div>
                    <p className="thread-controls-title">Integrity Audit</p>
                    <p className="integrity-audit-subtext" title="Epistemic oversight">Epistemic oversight</p>
                    <p className="hint">Shows narrative evidence notes after replies when enabled.</p>
                  </div>
                  <label className="quotha-toggle" htmlFor="quotha-toggle">
                    <input
                      id="quotha-toggle"
                      type="checkbox"
                      checked={quothaCommentaryEnabled}
                      onChange={(event) => handleQuothaToggle(event.target.checked)}
                      aria-label="Toggle Integrity Audit for this thread"
                    />
                    <span className={`quotha-status${quothaCommentaryEnabled ? ' on' : ''}`} aria-hidden="true">
                      {quothaCommentaryEnabled ? 'On' : 'Off'}
                    </span>
                  </label>
                </div>

                <TypingAnimationToggle
                  enabled={typingAnimationEnabled}
                  preference={typingAnimationPreference}
                  blocked={typingAnimationBlocked}
                  onToggle={setTypingAnimationEnabled}
                  description="Smoothly reveals assistant replies."
                  className="chat-typing-toggle-panel"
                />

                <QuickControlsCard
                  values={pendingSettings}
                  defaults={accountSettings}
                  onChange={handleControlChange}
                  onSave={handleSaveControls}
                  saving={controlsSaving}
                  saved={controlsSaved}
                  error={controlsError}
                  quothaSupported={false}
                  variant="full"
                  saveLabel="Save thread controls"
                />
              </div>
            )}
          </div>
        </aside>
      </div>
      {snippetModal && (
        <div className="snippet-modal-overlay" role="presentation" onClick={closeSnippetModal}>
          <div
            className="snippet-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="context-snippet-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="snippet-modal-header">
              <div>
                <h2 id="context-snippet-title">{snippetModal.sourceTitle}</h2>
                {snippetModal.sourceByline && <p className="snippet-modal-byline">{snippetModal.sourceByline}</p>}
              </div>
              <button type="button" className="snippet-modal-close" onClick={closeSnippetModal} aria-label="Close snippet modal">
                Close
              </button>
            </div>
            {(snippetModal.publicationDate || snippetModal.sourceType || snippetModal.partLabel) && (
              <div className="snippet-modal-meta">
                {snippetModal.publicationDate && <span>Published: {snippetModal.publicationDate}</span>}
                {snippetModal.sourceType && <span>Type: {snippetModal.sourceType}</span>}
                {snippetModal.partLabel && <span>Reference: {snippetModal.partLabel}</span>}
              </div>
            )}
            {snippetModal.sourceUrl && (
              <a className="snippet-modal-original" href={snippetModal.sourceUrl} target="_blank" rel="noreferrer">
                Open original source
              </a>
            )}
            {snippetModal.loading ? (
              <p className="hint">Loading passage...</p>
            ) : snippetModal.error ? (
              <p className="snippet-modal-error">{snippetModal.error}</p>
            ) : (
              <>
                {snippetModal.excerptText && <pre className="snippet-modal-text">{snippetModal.excerptText}</pre>}
                {snippetModal.fullChunkText && (
                  <details className="snippet-modal-full-chunk">
                    <summary>Show full source passage</summary>
                    <pre className="snippet-modal-text">{snippetModal.fullChunkText}</pre>
                  </details>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

export default ChatThread
